import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { TextInput } from "../../../commons/formik";
import { ButtonContainer, Button } from "../../../commons/elements";
import {
  logout,
  storeNextUrl,
  verifyUserToken,
  resetAccountPassword
} from "../../../state/user/actions";
import Spinner from "../../../commons/elements/components/spinner";
import PrivacyTos from "../../../commons/privacy-policy-tos";
import Axios from "../../../utils/axios";
import LoadingState from "../../../utils/LoadingState";

const propTypes = {
  config: PropTypes.object,
  loadingState: PropTypes.instanceOf(LoadingState),
  // Map state to props
  isLoading: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string,
  passwordMsg: PropTypes.string,
  // Map dispatch to props
  savePasswordAction: PropTypes.func.isRequired
};

function ResetPassword(props) {
  const {
    config,
    loadingState,
    isSubmitting,
    isLoading,
    errorMsg,
    passwordMsg,
    savePasswordAction
  } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  useEffect(() => {
    Axios.init(
      config,
      loadingState,
      history,
      () => dispatch(logout()),
      (url) => {
        dispatch(storeNextUrl(url));
      }
    );

    // Verify account token
    dispatch(verifyUserToken(token));
  }, []);

  const initialValues = {
    password: "",
    confirmPassword: ""
  };

  const NewPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /(?:[A-Z].*[0-9])|(?:[0-9].*[A-Z])/g,
        "Include at least one upper case letter and a number"
      )
      .required("Please enter a password"),
    confirmPassword: Yup.mixed()
      .oneOf([Yup.ref("password"), null], "Passwords don't match")
      .required("Please confirm your password")
  });

  const handleSubmit = (values) => {
    savePasswordAction(token, values.password);
  };

  return (
    <div
      role="region"
      className="main-content-area d-flex flex-column container-fluid"
    >
      <h1>Please Create A New Password</h1>
      {isLoading && (
        <div className="main-content-container h-100 w-100">
          <Spinner />
        </div>
      )}
      {!isLoading && (
        <div className="reset-pwd-section">
          <div className="main-content-container">
            {!!errorMsg && (
              <p
                style={{
                  fontSize: "large",
                  color: "red",
                  marginTop: 5
                }}
              >
                {errorMsg}
              </p>
            )}
            {!!passwordMsg && (
              <p
                style={{
                  fontSize: "large",
                  color: "green",
                  marginTop: 5
                }}
              >
                {passwordMsg}
              </p>
            )}
            <div className="form-container">
              <Formik
                initialValues={initialValues}
                validationSchema={NewPasswordSchema}
                onSubmit={handleSubmit}
              >
                {({ isValid, dirty }) => (
                  <Form>
                    <TextInput
                      id="create-account-password"
                      label="Password"
                      name="password"
                      type="password"
                      placeholder="password"
                      isDisabled={errorMsg || passwordMsg ? "true" : "false"}
                      labelProps={{ className: "lbl-password" }}
                    />
                    <TextInput
                      id="create-account-confirm-password"
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      placeholder="password"
                      isDisabled={errorMsg || passwordMsg ? "true" : "false"}
                      labelProps={{ className: "lbl-confirm-password" }}
                    />
                    <ButtonContainer>
                      <Button
                        className="reset-btn"
                        type="submit"
                        label="Submit"
                        disabled={
                          !(isValid && dirty) ||
                          isSubmitting ||
                          !!errorMsg ||
                          !!passwordMsg
                        }
                        isSubmitting={isSubmitting}
                      />
                    </ButtonContainer>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div style={{ marginLeft: 25 }}>
            <p>
              <strong>Secure Password Tips</strong>
            </p>
            <ul>
              <li>
                Use at least 8 characters and a combination of numbers, letters
                and characters is best.
              </li>
              <li>
                Do not use the same password that you have used previously.
              </li>
              <li>
                Do not use dictionary words, your name, e-mail address, or other
                personal information that can be easily obtained.
              </li>
              <li>
                Do not use the same password for multiple online accounts.
              </li>
            </ul>
          </div>
        </div>
      )}
      <PrivacyTos />
    </div>
  );
}

ResetPassword.propTypes = propTypes;

const mapStateToProps = (state) => ({
  isLoading: state.user.isLoading,
  isSubmitting: state.user.isSubmitting,
  errorMsg: state.user.tokenError,
  passwordMsg: state.user.message
});

const mapDispatchToProps = {
  savePasswordAction: resetAccountPassword
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
